// 위젯 B4 — 어떠한 서비스가 있는지 (★3 전체 서비스 대시보드)
// 원천: 남양주시 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 · Setting.care_service_types 카탈로그
//
// 시각 원칙: 한 눈에 "우리 권역이 제공 가능한 서비스가 무엇인지" 파악되도록
// 장기요양·비장기요양·응급 3그룹으로 나누고, 각 서비스마다 기관 수 + 이번주 의뢰 흐름을 노출.
// 미제공(기관 0) 서비스는 회색, 미응답 누적은 주황 배지로 즉시 눈에 띄게 한다.
import * as React from "react";
import { ServiceCatalogDrilldownModal } from "../../ServiceCatalogDrilldownModal";

interface ServiceItem {
  code: string;
  label: string;
  orgs: number;
  weekly_referrals: number;
  pending_referrals: number;
  status: "ok" | "backlogged" | "unavailable";
}

interface Group {
  group: string;
  group_label: string;
  services: ServiceItem[];
}

interface Props {
  data: {
    region: { name: string; code: string };
    generated_at?: string;
    totals: { orgs: number; weekly_referrals: number; pending: number };
    groups: Group[];
  };
}

const GROUP_COLOR: Record<string, string> = {
  ltc: "sky",
  non_ltc: "green",
  emergency: "rose",
};

// 그룹 슬러그 → 표시 라벨. 서브타이틀 클릭 버튼용 (short 라벨).
const GROUP_SHORT_LABEL: Record<string, string> = {
  ltc: "장기요양",
  non_ltc: "비장기요양",
  emergency: "응급",
};

export function ServiceCatalog({ data }: Props) {
  // 카드 클릭 → 그 급여의 제공 기관/의뢰 목록(드릴다운 모달).
  // 예전엔 /organizations?service_code=... 로 이동만 했고 그 화면이 급여 필터를
  // 몰라 전체 목록이 떴다. { svc, view } 형태로 어떤 탭으로 열지도 함께 들고 있다.
  const [drilldown, setDrilldown] = React.useState<{ svc: ServiceItem; view: string } | null>(null);

  // 그룹 라벨 클릭 시 해당 섹션으로 스크롤. 위젯 내 앵커(nyj-catalog-*)로 이동.
  const jumpToGroup = (slug: string) => {
    const el = document.getElementById(`nyj-catalog-${slug}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "18px 22px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="h3">남양주 서비스 카탈로그</div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <span>시민이 이용 가능한 서비스 ·</span>
            {["ltc", "non_ltc", "emergency"].map((slug, i, arr) => (
              <React.Fragment key={slug}>
                <button
                  type="button"
                  onClick={() => jumpToGroup(slug)}
                  title={`${GROUP_SHORT_LABEL[slug]} 그룹으로 이동`}
                  style={{
                    background: "transparent", border: "none",
                    padding: "2px 6px", borderRadius: 6,
                    color: `var(--${GROUP_COLOR[slug]}-600)`,
                    fontWeight: 700, fontSize: 11,
                    cursor: "pointer",
                    textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3,
                    font: "inherit",
                  }}
                >
                  {GROUP_SHORT_LABEL[slug]}
                </button>
                {i < arr.length - 1 && <span style={{ color: "var(--ink-4)" }}>·</span>}
              </React.Fragment>
            ))}
            <span>·</span>
            <span style={{ color: "var(--orange-600)", fontWeight: 600 }}>카드 클릭 시 해당 건 목록</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className="badge badge-gray">기관 <b style={{ marginLeft: 4 }}>{data.totals.orgs}</b></span>
          <span className="badge badge-sky">이번주 의뢰 <b style={{ marginLeft: 4 }}>{data.totals.weekly_referrals}</b></span>
          {data.totals.pending > 0 && (
            <span className="badge badge-amber">미응답 <b style={{ marginLeft: 4 }}>{data.totals.pending}</b></span>
          )}
        </div>
      </div>

      <div style={{ padding: "8px 22px 22px" }}>
        {data.groups.map((g, gi) => {
          const color = GROUP_COLOR[g.group] || "gray";
          return (
            <div key={g.group} id={`nyj-catalog-${g.group}`} style={{
              marginBottom: gi < data.groups.length - 1 ? 18 : 0,
              scrollMarginTop: 12,
            }}>
              <div style={{
                display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10, paddingLeft: 2,
                paddingBottom: 6, borderBottom: `2px solid var(--${color}-500)`,
              }}>
                <span style={{
                  fontSize: 12, fontWeight: 800, color: `var(--${color}-600)`,
                  letterSpacing: 0.5,
                  padding: "2px 8px", background: `var(--${color}-50)`, borderRadius: 6,
                }}>
                  {g.group_label}
                </span>
                <span className="muted mono" style={{ fontSize: 10.5 }}>{g.services.length}종</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                {g.services.map((s) => (
                  <ServiceCell key={s.code} svc={s} colorToken={color}
                    onOpen={(view) => setDrilldown({ svc: s, view })} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {drilldown && (
        <ServiceCatalogDrilldownModal
          service={{ code: drilldown.svc.code, label: drilldown.svc.label }}
          regionCode={data.region?.code}
          initialView={drilldown.view}
          onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}

function ServiceCell({ svc, colorToken, onOpen }: { svc: ServiceItem; colorToken: string; onOpen: (view: string) => void }) {
  const [hover, setHover] = React.useState(false);
  const available = svc.status !== "unavailable";
  const accent = available ? `var(--${colorToken}-500)` : "var(--ink-4)";
  const accentDeep = available ? `var(--${colorToken}-600)` : "var(--ink-3)";
  const tint = available ? `var(--${colorToken}-50)` : "var(--bg-sub)";

  // 클릭 시 이 급여에 해당하는 건(제공 기관·의뢰)만 담은 드릴다운을 연다.
  // 미제공(기관 0) 셀은 보여 줄 기관이 없으므로 비활성 그대로 둔다.
  const handleClick = () => {
    if (!available) return;
    onOpen("orgs");
  };
  const handleKey = (e: React.KeyboardEvent) => {
    if (available && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role={available ? "button" : undefined}
      tabIndex={available ? 0 : -1}
      onClick={available ? handleClick : undefined}
      onKeyDown={handleKey}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={
        svc.status === "unavailable" ? `${svc.label} — 등록된 제공기관이 없습니다` :
        svc.status === "backlogged"  ? `${svc.label} — 미응답 의뢰 ${svc.pending_referrals}건 (확인 필요) · 클릭 시 해당 건 목록` :
        `${svc.label} · 기관 ${svc.orgs}개 · 이번주 의뢰 ${svc.weekly_referrals}건 · 클릭 시 해당 건 목록`
      }
      style={{
        background: "var(--surface)",
        border: `1px solid ${hover && available ? accent : "var(--border-soft)"}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 10,
        padding: "10px 12px",
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        transform: hover && available ? "translateY(-2px)" : "none",
        boxShadow: hover && available ? `0 6px 16px -6px ${accent}, 0 2px 4px rgba(0,0,0,0.04)` : "none",
        opacity: available ? 1 : 0.6,
        cursor: available ? "pointer" : "default",
        display: "flex", flexDirection: "column", gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontSize: 12, color: "var(--ink-1)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
          {svc.label}
        </span>
        <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)", fontWeight: 600, flexShrink: 0 }}>{svc.code}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: accentDeep, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
          {svc.orgs}
        </span>
        <span className="muted" style={{ fontSize: 10.5 }}>기관</span>
        {svc.weekly_referrals > 0 && (
          <span role="button" tabIndex={0}
            title={`이번주 의뢰 ${svc.weekly_referrals}건 — 이 급여의 의뢰 목록 보기`}
            onClick={(e) => { e.stopPropagation(); onOpen("referrals-all"); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpen("referrals-all"); } }}
            className="muted" style={{ fontSize: 10.5, marginLeft: "auto", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}>
            의뢰 <b style={{ color: "var(--ink-2)", fontWeight: 700 }}>+{svc.weekly_referrals}</b>
          </span>
        )}
      </div>
      {svc.status === "backlogged" && (
        <div role="button" tabIndex={0}
          title={`미응답 의뢰 ${svc.pending_referrals}건만 보기`}
          onClick={(e) => { e.stopPropagation(); onOpen("referrals"); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onOpen("referrals"); } }}
          style={{ marginTop: 4, padding: "3px 7px", background: "var(--amber-50)", borderRadius: 6, alignSelf: "flex-start", fontSize: 10, color: "var(--amber-500)", fontWeight: 700, cursor: "pointer" }}>
          미응답 {svc.pending_referrals}
        </div>
      )}
      {svc.status === "unavailable" && (
        <div style={{ marginTop: 4, fontSize: 10, color: "var(--ink-4)", fontWeight: 600 }}>
          등록된 제공기관 없음
        </div>
      )}
    </div>
  );
}
